import changelog from '@/app-changelog.js';
import { CHANGELOG_UNREAD_PERIOD_DAYS } from '@/config';

export const CHANGELOG_READ_STORAGE_KEY = 'app.changelog.readItems';

export function normalizeChangelogRole(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  const aliases = {
    clubadmin: 'club-admin',
    'club-administrator': 'club-admin',
  };
  return aliases[normalized] || normalized;
}

export function getChangelogRoleKeys(user) {
  const keys = new Set();
  const authState = user?.isAuthenticated;
  const isAuthenticated = authState && typeof authState === 'object' && 'value' in authState
    ? Boolean(authState.value)
    : Boolean(authState);

  if (isAuthenticated) keys.add('authenticated');

  [
    user?.role?.type,
    user?.role?.name,
  ]
    .filter(Boolean)
    .map((value) => normalizeChangelogRole(value))
    .forEach((value) => keys.add(value));

  if (user?.isSuperAdmin || keys.has('superadmin')) {
    keys.add('superadmin');
    keys.add('club-admin');
  }

  return Array.from(keys);
}

export function canShowChangelogItemForRoles(roles, roleKeys = []) {
  if (!Array.isArray(roles) || roles.length === 0) return true;
  const normalized = roles.map((role) => normalizeChangelogRole(role));
  return normalized.some((role) => roleKeys.includes(role));
}

export function getChangelogEntryKey(entry, entryIndex) {
  return `${entry?.version || 'no-version'}:${entry?.date || 'no-date'}:${entry?.title || entryIndex}`;
}

export function getChangelogItemKey(entry, entryIndex, itemIndex) {
  return `${getChangelogEntryKey(entry, entryIndex)}:${itemIndex}`;
}

export function isChangelogEntryUnreadEligible(entry, now = new Date()) {
  const dateMatch = String(entry?.date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return false;

  const [, year, month, day] = dateMatch;
  const entryDate = new Date(Number(year), Number(month) - 1, Number(day));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const cutoffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - CHANGELOG_UNREAD_PERIOD_DAYS + 1);

  return entryDate >= cutoffDate && entryDate <= today
    && entryDate.getFullYear() === Number(year)
    && entryDate.getMonth() === Number(month) - 1
    && entryDate.getDate() === Number(day);
}

export function loadChangelogReadItemKeys() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CHANGELOG_READ_STORAGE_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export function getVisibleChangelogItemKeys(user) {
  const roleKeys = getChangelogRoleKeys(user);

  return (Array.isArray(changelog) ? changelog : []).flatMap((entry, entryIndex) => {
    if (!isChangelogEntryUnreadEligible(entry)) return [];

    const items = Array.isArray(entry?.items) ? entry.items : [];
    return items
      .map((item, itemIndex) => ({
        key: getChangelogItemKey(entry, entryIndex, itemIndex),
        roles: typeof item === 'string' ? [] : item?.roles,
      }))
      .filter((item) => canShowChangelogItemForRoles(item.roles, roleKeys))
      .map((item) => item.key);
  });
}

export function hasUnreadChangelogItems(user) {
  const readKeys = loadChangelogReadItemKeys();
  return getVisibleChangelogItemKeys(user).some((key) => !readKeys.includes(key));
}
